// Task Template Manager
// Main component for managing task templates

import React, { useState } from 'react';
import { useTaskTemplates } from '../../../hooks/useTaskTemplates';
import { useTaskTypes } from '../../../hooks/useTaskNomenclatures';
import TaskTemplateList from './TaskTemplateList';
import TaskTemplateForm from './TaskTemplateForm';
import './TaskTemplateManager.css';

const TaskTemplateManager = ({ userId }) => {
  const [activeView, setActiveView] = useState('list'); // 'list' or 'form'
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [filters, setFilters] = useState({
    searchTerm: '',
    taskType: '',
    isActive: true
  });

  const { templates, loading, error, refresh } = useTaskTemplates(filters);
  const { items: taskTypes } = useTaskTypes();

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setActiveView('form');
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setActiveView('form');
  };

  const handleFormSave = () => {
    setActiveView('list');
    setSelectedTemplate(null);
    refresh();
  };

  const handleFormCancel = () => {
    setActiveView('list');
    setSelectedTemplate(null);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (activeView === 'form') {
    return (
      <TaskTemplateForm
        template={selectedTemplate}
        userId={userId}
        onSave={handleFormSave}
        onCancel={handleFormCancel}
      />
    );
  }

  return (
    <div className="task-template-manager">
      <div className="template-manager-header">
        <div>
          <h2>Task Templates</h2>
          <p className="subtitle">Create and manage reusable task templates</p>
        </div>
        <button className="btn btn-primary" onClick={handleCreateTemplate}>
          + Create Template
        </button>
      </div>

      {/* Filters */}
      <div className="template-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search templates..."
            value={filters.searchTerm}
            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <select
            value={filters.taskType}
            onChange={(e) => handleFilterChange('taskType', e.target.value)}
            className="filter-select"
          >
            <option value="">All Task Types</option>
            {taskTypes && taskTypes.map(type => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filters.isActive ? 'active' : 'inactive'}
            onChange={(e) => handleFilterChange('isActive', e.target.value === 'active')}
            className="filter-select"
          >
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
            <option value="">All Templates</option>
          </select>
        </div>
      </div>

      {/* Template List */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading templates...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>Error loading templates: {error}</p>
          <button onClick={refresh} className="btn btn-secondary">Retry</button>
        </div>
      ) : (
        <TaskTemplateList
          templates={templates}
          onEdit={handleEditTemplate}
          onRefresh={refresh}
          userId={userId}
        />
      )}
    </div>
  );
};

export default TaskTemplateManager;


